import {Edge, Node} from 'reactflow';

import {assert} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentMethod} from 'studies/typed/shared/ExperimentalInputs';
import {BaseCondition, ConditionGroup} from 'studies/typed/shared/useExperimentDesignState';
import {hideFlowchartElementBeforeLayout} from 'ui-library/reactflow/layout/hideFlowchartElementBeforeLayoutUtils';

import {
  createProcedureRunDesignFlowchartColumnNode,
  FALLBACK_COLUMN_NODE_HEIGHT,
  ProcedureRunDesignFlowchartColumnNodeData,
} from './ProcedureRunDesignFlowchartColumnNode';
import {
  makeProcedureRunDesignFlowchartEdge,
  ProcedureRunDesignFlowchartEdgeData,
  ProcedureRunDesignFlowchartEdgeVariant,
} from './ProcedureRunDesignFlowchartEdge';
import {
  createProcedureRunDesignFlowchartNode,
  FALLBACK_NODE_WIDTH,
  FALLBACK_NODE_HEIGHT,
  getProcedureRunDesignFlowchartNodeId,
  ProcedureRunDesignFlowchartNodeData,
} from './ProcedureRunDesignFlowchartNode';
import {UnitOperationStatusType} from './useMultiProcedureRunStudyProgress';

/** Maps a flowchart node id to its method task status. Write and read paths must use matching keys. */
export type StatusByNodeId = ReadonlyMap<string, UnitOperationStatusType>;

/** A directed link between two grid nodes. Both planning and execution edges reduce to this primitive. */
export interface FlowchartEdgeSpec {
  sourceNodeId: string;
  targetNodeId: string;
  /** Defaults to 'default'. */
  variant?: ProcedureRunDesignFlowchartEdgeVariant;
}

export interface GridLayoutOptions {
  horizontalGap: number;
  verticalGap: number;
  startX: number;
  startY: number;
}

/**
 * Creates the initial set of hidden nodes from domain data. This is the single place that maps
 * domain objects (conditions, methods) to ReactFlow nodes. All nodes start with opacity 0% so
 * ReactFlow can measure them before the first visible frame.
 */
export function makeInitialNodes(
  conditions: Array<BaseCondition>,
  methods: Array<ExperimentMethod>,
  procedureType: ProcedureType,
  activeMethodId: string | null = null,
  statusByNodeId?: StatusByNodeId
): {
  columnNodes: Array<Node<ProcedureRunDesignFlowchartColumnNodeData>>;
  rows: Array<Array<Node<ProcedureRunDesignFlowchartNodeData>>>;
} {
  const activeMethodIndex =
    activeMethodId == null ? -1 : methods.findIndex((method) => method.id === activeMethodId);
  const columnNodes = methods.map((method, methodIndex) =>
    hideFlowchartElementBeforeLayout(
      createProcedureRunDesignFlowchartColumnNode({
        methodId: method.id,
        methodName: method.name,
        factorsSet: 0, // TODO(BNCH-153703): Add factors set
        procedureType,
        isActiveMethod: method.id === activeMethodId,
        isHighlighted: false,
        isUpstreamOfActiveMethod: activeMethodIndex > 0 && methodIndex < activeMethodIndex,
      })
    )
  );

  const rows: Array<Array<Node<ProcedureRunDesignFlowchartNodeData>>> = [];
  conditions.forEach((condition, conditionIndex) => {
    for (let replicateIndex = 0; replicateIndex < condition.numReplicates; replicateIndex++) {
      rows.push(
        methods.map((method) =>
          hideFlowchartElementBeforeLayout(
            createProcedureRunDesignFlowchartNode({
              conditionIndex,
              replicateIndex,
              methodId: method.id,
              conditionName: condition.name,
              replicateNumber: replicateIndex + 1,
              conditionDescription: condition.description,
              isHovered: false,
              isHighlighted: false,
              status: statusByNodeId?.get(
                getProcedureRunDesignFlowchartNodeId({
                  conditionIndex,
                  replicateIndex,
                  methodId: method.id,
                })
              ),
            })
          )
        )
      );
    }
  });

  return {columnNodes, rows};
}

/**
 * Maps {@link FlowchartEdgeSpec}s to hidden ReactFlow edges wired to each endpoint's deterministic
 * handles. Edges start hidden so they reveal atomically with their nodes once layout settles.
 */
export function makeInitialEdges(
  edgeSpecs: Array<FlowchartEdgeSpec>
): Array<Edge<ProcedureRunDesignFlowchartEdgeData>> {
  return edgeSpecs.map((spec) => hideFlowchartElementBeforeLayout(makeProcedureRunDesignFlowchartEdge(spec)));
}

/** What's needed to place a PMEI in the design grid: its template method (column) and condition-replicate (row). */
export interface MethodExecutionInstanceInfoForPlacement {
  pmeiId: string;
  methodId: string;
  conditionId: string;
  replicateNumber: number;
}

/**
 * A directed edge in a `ProcedureRun`'s runtime condition graph, from a source PMEI to a target PMEI.
 */
export interface ConditionReplicateEdge {
  sourcePmeiId: string;
  targetPmeiId: string;
}

/** A PMEI resolved to the grid node that renders it, plus its template method ID (for downstream styling). */
interface ResolvedPmeiNode {
  nodeId: string;
  methodId: string;
}

/**
 * Maps each persisted `ProcedureRunCondition` id to its rendered condition group index (the grid row).
 * A condition id belongs to exactly one group. Shared by the status map and edge resolver.
 */
export function buildConditionGroupIndexByConditionId(
  conditions: Array<ConditionGroup>
): ReadonlyMap<string, number> {
  return new Map<string, number>(
    conditions.flatMap((conditionGroup, index) =>
      Object.values(conditionGroup.conditionIdsByPmdvId).map((conditionId) => [conditionId, index])
    )
  );
}

/**
 * Maps each PMEI id to the grid node that renders it, skipping PMEIs whose condition isn't rendered.
 * Asserts the persisted `replicateNumber` is within the rendered group's `numReplicates`: today the live
 * design can't drop a persisted replicate, so an out-of-range value is a data bug, not an edit.
 */
export function buildPmeiToNodeIdMap(
  infoForPlacements: Array<MethodExecutionInstanceInfoForPlacement>,
  conditions: Array<ConditionGroup>
): ReadonlyMap<string, ResolvedPmeiNode> {
  const conditionIndexById = buildConditionGroupIndexByConditionId(conditions);
  const numReplicatesByConditionId = new Map<string, number>(
    conditions.flatMap((conditionGroup) =>
      Object.values(conditionGroup.conditionIdsByPmdvId).map((conditionId): [string, number] => [
        conditionId,
        conditionGroup.numReplicates,
      ])
    )
  );
  const pmeiToNode = new Map<string, ResolvedPmeiNode>();
  infoForPlacements.forEach(({pmeiId, methodId, conditionId, replicateNumber}) => {
    const conditionIndex = conditionIndexById.get(conditionId);
    if (conditionIndex == null) {
      return;
    }
    const numReplicates = numReplicatesByConditionId.get(conditionId);
    assert(
      numReplicates != null && replicateNumber >= 1 && replicateNumber <= numReplicates,
      `PMEI ${pmeiId} has replicateNumber ${replicateNumber} outside the rendered range [1, ${numReplicates}] for condition ${conditionId}`
    );
    pmeiToNode.set(pmeiId, {
      nodeId: getProcedureRunDesignFlowchartNodeId({
        conditionIndex,
        replicateIndex: replicateNumber - 1,
        methodId,
      }),
      methodId,
    });
  });
  return pmeiToNode;
}

/**
 * Returns the largest measured height among the given nodes, falling back to `fallbackHeight`
 * when none of them have been measured yet (height null/0). Used to position grid rows below the
 * column headers using their real rendered height rather than a static estimate.
 */
export function getMaxMeasuredNodeHeight<T>(nodes: Array<Node<T>>, fallbackHeight: number): number {
  const measuredHeights = nodes.map((node) => node.height ?? 0).filter((height) => height > 0);
  return measuredHeights.length > 0 ? Math.max(...measuredHeights) : fallbackHeight;
}

/**
 * Purely geometric grid layout. Takes measured node dimensions from ReactFlow and computes
 * absolute positions for each node. Returns repositioned rows and per-column x/width values
 * for use by recomputeColumnPositions.
 *
 * rows[r][c] must have width/height populated by ReactFlow before calling this.
 */
export function recomputeGridPositions<T>(
  rows: Array<Array<Node<T>>>,
  {horizontalGap, verticalGap, startX, startY}: GridLayoutOptions
): {
  repositionedRows: Array<Array<Node<T>>>;
  columnDimensions: Array<{x: number; width: number}>;
} {
  if (rows.length === 0 || rows[0].length === 0) {
    return {repositionedRows: rows, columnDimensions: []};
  }

  // Each column is as wide as its widest grid cell. Map over the first row to get one width per
  // column (every row has the same number of columns).
  const columnWidths = rows[0].map((_, col) =>
    Math.max(...rows.map((row) => row[col]?.width ?? FALLBACK_NODE_WIDTH))
  );
  const columnDimensions = computeColumnDimensions(columnWidths, {startX, horizontalGap});

  // Reposition rows with cumulative Y, using the tallest node in each row.
  let currentY = startY;
  const repositionedRows = rows.map((row) => {
    const rowHeight = Math.max(
      ...row.map((node) => (node.height != null && node.height > 0 ? node.height : FALLBACK_NODE_HEIGHT))
    );
    const repositionedRow = row.map((node, col) => ({
      ...node,
      position: {x: columnDimensions[col].x, y: currentY},
    }));
    currentY += rowHeight + verticalGap;
    return repositionedRow;
  });

  return {repositionedRows, columnDimensions};
}

/**
 * Lays out the given column widths left-to-right, advancing x by `width + horizontalGap` per column.
 * Single source of truth for column x-spacing, using the widths from the widest grid cell per column.
 */
function computeColumnDimensions(
  widths: Array<number>,
  {startX, horizontalGap}: {startX: number; horizontalGap: number}
): Array<{x: number; width: number}> {
  let currentX = startX;
  return widths.map((width) => {
    const dimension = {x: currentX, width};
    currentX += width + horizontalGap;
    return dimension;
  });
}

/**
 * Positions and sizes column overlay nodes using the column dimensions computed by
 * recomputeGridPositions. Width matches the widest node in each column; height spans the
 * full column from header through the last grid row.
 */
export function recomputeColumnPositions<T>(
  columnNodes: Array<Node<T>>,
  columnDimensions: Array<{x: number; width: number}>,
  {startY, totalHeight}: {startY: number; totalHeight: number}
): Array<Node<T>> {
  assert(
    columnNodes.length === columnDimensions.length,
    'columnNodes and columnDimensions must have the same length'
  );

  return columnNodes.map((node, index) => {
    const dimension = columnDimensions[index];
    return {
      ...node,
      position: {x: dimension.x, y: startY},
      style: {...node.style, width: dimension.width, height: totalHeight},
    };
  });
}

/** Height from column top (startY) through the bottom of the last grid row. */
export function computeColumnTotalHeight(repositionedRows: Array<Array<Node>>, columnStartY: number): number {
  if (repositionedRows.length === 0) {
    return FALLBACK_COLUMN_NODE_HEIGHT;
  }
  const lastRow = repositionedRows[repositionedRows.length - 1];
  if (lastRow.length === 0) {
    return FALLBACK_COLUMN_NODE_HEIGHT;
  }
  const lastRowY = lastRow[0].position.y;
  const lastRowHeight = Math.max(
    ...lastRow.map((node) => (node.height != null && node.height > 0 ? node.height : FALLBACK_NODE_HEIGHT))
  );
  return lastRowY + lastRowHeight - columnStartY;
}
